// src/components/texting/TextThread.jsx
// Chat-style SMS thread for one lead or deal. Reused by TextThreadModal
// (buyer lead drawer) and the deal-detail "texts" tab. Live via onSnapshot;
// sends via the functions-api /rc-send endpoint (team line, Slice 1).
import React, { useEffect, useRef, useState } from "react";
import { Send, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { getContactsByIds } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import {
  normalizePhoneE164,
  subscribeTextThread,
  subscribeThreadForNumber,
  sendText,
  markThreadRead,
  cancelScheduledText,
  suggestReply,
  canUseAi,
  useFromNumberOptions,
  savePreferredFrom,
} from "../../utils/texting";

const fmtNumber = (e164) => {
  const d = String(e164 || "").replace(/\D/g, "");
  return d.length === 11
    ? `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`
    : e164;
};

const fmtTime = (ms) =>
  new Date(ms).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export default function TextThread({
  leadId,
  dealId,
  contactId,
  contactIds, // optional: ALL clients on a deal (group send to the owners)
  contactName,
  phone: phoneProp,
  initialBody, // optional: pre-fills the composer (e.g. a Past Client Follow-Up draft)
}) {
  const [{ user }] = useStateValue();
  const [messages, setMessages] = useState(null); // null = loading
  const [phone, setPhone] = useState(normalizePhoneE164(phoneProp || ""));
  const [phoneLoaded, setPhoneLoaded] = useState(!!phoneProp);
  const [draft, setDraft] = useState(initialBody || "");
  const [pending, setPending] = useState([]); // optimistic "sending…" bubbles
  const [fromNumber, setFromNumber] = useState("");
  const [extraPhones, setExtraPhones] = useState([]); // co-owners on a deal
  const [scheduleAt, setScheduleAt] = useState(""); // datetime-local value
  const [showSchedule, setShowSchedule] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const handleSuggest = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    const res = await suggestReply({ leadId, dealId });
    setAiBusy(false);
    if (res.ok) setDraft(res.draft);
    else toast.error(res.error || "Could not draft a reply");
  };
  const bottomRef = useRef(null);
  const composerRef = useRef(null); // auto-growing textarea

  // Keep the composer sized to its content even when the text is set
  // programmatically (✨ drafts, clears after send).
  useEffect(() => {
    const el = composerRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  }, [draft]);

  // Deal group send: resolve EVERY client's number on the file.
  useEffect(() => {
    if (!Array.isArray(contactIds) || contactIds.length < 2) {
      setExtraPhones([]);
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const raws = await getContactsByIds(contactIds);
        const phones = (raws || [])
          .map((r) => normalizePhoneE164(normalizeContact(r)?.phone || ""))
          .filter(Boolean);
        if (alive) setExtraPhones([...new Set(phones)]);
      } catch (e) {
        console.error("TextThread group phone lookup failed:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [contactIds]);

  // From picker options + the user's remembered default (sticks per person).
  const { options: fromOptions, defaultNumber } = useFromNumberOptions(user);
  useEffect(() => {
    setFromNumber((prev) => prev || defaultNumber);
  }, [defaultNumber]);

  // Live thread. Number-only conversations (unmatched inbound, mass-text
  // recipients) load by phone number so the panel never spins forever.
  useEffect(() => {
    if (leadId || dealId) {
      return subscribeTextThread({ leadId, dealId }, setMessages);
    }
    const num = normalizePhoneE164(phoneProp || "");
    if (num) {
      return subscribeThreadForNumber(num, setMessages);
    }
    setMessages([]); // nothing to load: empty state beats an endless spinner
    return undefined;
  }, [leadId, dealId, phoneProp]);

  // Client phone from the contact record (skipped when the caller already
  // knows the number, e.g. the Texting Center inbox).
  useEffect(() => {
    if (phoneProp) return undefined;
    let alive = true;
    (async () => {
      try {
        if (contactId) {
          const raw = (await getContactsByIds([contactId]))[0];
          const p = normalizeContact(raw)?.phone || "";
          if (alive) setPhone(normalizePhoneE164(p));
        }
      } catch (e) {
        console.error("TextThread phone lookup failed:", e);
      } finally {
        if (alive) setPhoneLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [contactId, phoneProp]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  // An open thread counts as read — clears the 💬 badges for everyone.
  useEffect(() => {
    if (!messages?.some((m) => m.direction === "inbound" && m.read === false))
      return;
    markThreadRead({ leadId, dealId });
  }, [messages, leadId, dealId]);

  // All numbers this thread sends to (group send when a deal has co-owners).
  const targetNumbers =
    extraPhones.length > 1
      ? extraPhones
      : phone
        ? [phone]
        : [];

  // Optimistic send: the bubble appears INSTANTLY marked "sending…"; the
  // confirmed doc arrives via the live listener and replaces it. A future
  // scheduleAt turns Send into Schedule (fires within ~10 min of the time).
  const doSend = async (bodyOverride) => {
    const body = String(bodyOverride ?? draft).trim();
    if (!body || targetNumbers.length === 0) return;
    const sendAtMs = scheduleAt ? new Date(scheduleAt).getTime() : 0;
    if (scheduleAt && !(sendAtMs > Date.now())) {
      toast.error("Pick a time in the future, or clear the schedule.");
      return;
    }
    const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    if (!sendAtMs) {
      setPending((p) => [
        ...p,
        {
          id: tempId,
          direction: "outbound",
          body,
          fromNumber,
          status: "sending",
          sentByName:
            [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "",
          createdAt: Date.now(),
        },
      ]);
    }
    setDraft("");
    if (composerRef.current) composerRef.current.style.height = "auto";
    const res = await sendText({
      to: targetNumbers.length > 1 ? targetNumbers : targetNumbers[0],
      body,
      fromNumber,
      sendAt: sendAtMs,
      leadId,
      dealId,
      contactName,
      user,
    });
    setPending((p) => p.filter((m) => m.id !== tempId));
    if (!res.ok) {
      toast.error(res.error || "Text failed to send");
    } else if (res.scheduled) {
      setScheduleAt("");
      setShowSchedule(false);
      toast.success("Scheduled. You can cancel it from the thread until it sends.");
    }
  };

  // Merge confirmed + pending (dropping a pending bubble the moment its
  // confirmed twin shows up, so nothing ever doubles). Cancelled scheduled
  // messages stay hidden.
  const displayMessages =
    messages === null
      ? null
      : [...messages.filter((m) => m.status !== "cancelled"), ...pending].sort(
          (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
        );

  const canSend = targetNumbers.length > 0;

  return (
    <div className="flex flex-col h-full min-h-[320px]">
      <div className="flex-1 overflow-y-auto bg-white rounded-lg p-3 space-y-1">
        {displayMessages === null ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm p-3">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </div>
        ) : displayMessages.length === 0 ? (
          <p className="text-sm text-slate-400 p-3">
            No texts yet{contactName ? ` with ${contactName}` : ""}. Messages you
            send show up here — and stay on this record for the whole team.
          </p>
        ) : (
          displayMessages.map((m) => (
            <div key={m.id} className="flex flex-col">
              <div
                className={`max-w-[80%] px-3.5 py-2 rounded-[18px] text-[15px] leading-snug whitespace-pre-wrap ${
                  m.direction === "outbound"
                    ? "self-end bg-[#007AFF] text-white rounded-br-[5px]"
                    : "self-start bg-[#E9E9EB] text-black rounded-bl-[5px]"
                }`}
              >
                {m.body}
              </div>
              <div
                className={`text-[11px] text-slate-400 mt-0.5 mb-1.5 ${
                  m.direction === "outbound" ? "self-end" : "self-start"
                }`}
              >
                {m.direction === "outbound"
                  ? `${m.sentByName || "Team"} · ${m.fromNumber ? fmtNumber(m.fromNumber) : "team line"}`
                  : contactName || "Client"}
                {" · "}
                {m.status === "sending" ? (
                  <span className="italic">sending…</span>
                ) : m.status === "scheduled" ? (
                  <>
                    <span className="italic text-linkColor">
                      ⏰ scheduled for {m.sendAt ? fmtTime(m.sendAt) : "later"}
                    </span>
                    <button
                      type="button"
                      onClick={() => cancelScheduledText(m.id)}
                      className="ml-1 text-red-500 font-semibold hover:underline"
                    >
                      cancel
                    </button>
                  </>
                ) : m.createdAt ? (
                  fmtTime(m.createdAt)
                ) : (
                  ""
                )}
                {m.groupSend && (
                  <span className="ml-1 text-linkColor font-semibold">
                    · to {m.toNumbers?.length || 2} people
                  </span>
                )}
                {m.status === "failed" && (
                  <button
                    type="button"
                    onClick={() => doSend(m.body)}
                    className="ml-1 inline-flex items-center gap-0.5 text-red-600 font-semibold hover:underline"
                  >
                    <AlertTriangle size={11} /> failed — tap to retry
                  </button>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <div className="pt-3">
        {phoneLoaded && !phone ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            No mobile number on this client's contact — add one to their contact
            record to text them.
          </p>
        ) : (
          <>
            {fromOptions.length > 1 && (
              <div className="flex items-center gap-1.5 mb-2 text-xs text-slate-500">
                From:
                <select
                  value={fromNumber}
                  onChange={(e) => {
                    setFromNumber(e.target.value);
                    savePreferredFrom(user?.id, e.target.value);
                  }}
                  className="text-xs font-semibold text-linkColor bg-slate-50 border border-slate-300 rounded-md px-2 py-1"
                >
                  {fromOptions.map((o) => (
                    <option key={o.num} value={o.num}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          {extraPhones.length > 1 && (
            <p className="text-[11px] text-linkColor font-semibold mb-1.5">
              To: all {extraPhones.length} clients on this file (one message,
              everyone gets it)
            </p>
          )}
          {showSchedule && (
            <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
              Send at:
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="text-xs border border-slate-300 rounded-md px-2 py-1"
              />
              {scheduleAt && (
                <button
                  type="button"
                  onClick={() => setScheduleAt("")}
                  className="text-red-500 font-semibold hover:underline"
                >
                  clear
                </button>
              )}
            </div>
          )}
          <div className="flex gap-2 items-center">
            {(leadId || dealId) && canUseAi(user) && (
              <button
                type="button"
                onClick={handleSuggest}
                disabled={aiBusy}
                title="Suggest a reply with AI"
                className="w-9 h-9 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-600 text-sm flex items-center justify-center disabled:opacity-50"
              >
                {aiBusy ? "…" : "✨"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowSchedule((v) => !v)}
              title="Schedule for later"
              className={`w-9 h-9 rounded-full border flex items-center justify-center text-sm ${
                scheduleAt
                  ? "border-themeColor bg-slate-50 text-linkColor"
                  : "border-slate-300 text-slate-400 hover:text-slate-600"
              }`}
            >
              ⏰
            </button>
            <textarea
              ref={composerRef}
              rows={1}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                // Grow with the text (like iMessage), up to ~5 lines.
                e.target.style.height = "auto";
                e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  doSend();
                }
              }}
              placeholder={contactName ? `Text ${contactName}…` : "Type a text…"}
              disabled={!canSend}
              className="flex-1 text-sm border border-slate-300 rounded-[18px] px-4 py-2 leading-snug resize-none overflow-y-auto focus:outline-none focus:border-themeColor disabled:bg-slate-50"
            />
            <button
              type="button"
              onClick={() => doSend()}
              disabled={!canSend || !draft.trim()}
              aria-label={scheduleAt ? "Schedule text" : "Send text"}
              className={`h-9 rounded-full text-white flex items-center justify-center disabled:opacity-40 ${
                scheduleAt ? "px-3 bg-themeColor text-xs font-bold" : "w-9 bg-themeColor"
              }`}
            >
              {scheduleAt ? "Schedule" : <Send size={15} />}
            </button>
          </div>
          </>
        )}
        <p className="text-[10px] text-slate-400 mt-1.5">
          Sends from {fromNumber ? fmtNumber(fromNumber) : "the team line"}
          {phone ? ` to ${phone}` : ""}. Visible to everyone who can see this
          record.
        </p>
      </div>
    </div>
  );
}
